import { push } from "connected-react-router";
import React from "react";
import { connect } from "react-redux";
import { Redirect, Route, RouteComponentProps, Router, Switch } from "react-router";
import { withRouter } from "react-router-dom";
import { Dispatch } from "redux";
import { IAction } from "reduxsauce";
import { Creators as AllCreators } from "../actions";
import { CourseAuthor } from "../components/course-author/CourseAuthor.container";
import { Dashboard } from "../components/dashboard/Dashboard.container";
import { NotFound } from "../components/not-found/NotFound";
import RoutesWithSideMenu from "../components/utils/SideMenu/RoutesWithSideMenu";
import Spinner from "../components/utils/Spinner";
import { getSkinUrls } from "../utils";
import { AccountList } from "./Account/Account.component";
import { Analytics } from "./Analytics/Analytics.container";
import { Classes } from "./Classes/Classes.container";
import { ClassManagement } from "./ClassManagement/ClassManagement.container";
import Courses from "./Courses";
import { ExportedScormPackages } from "./ExportedScormPackages/ExportedScormPackages.container";
import { Login } from "./Login/Login.container";
import { Logout } from "./Logout/Logout.container";
import OrganizationSettings from "./Organization/OrganizationSettings/OrganizationSettings.container";
import { IState } from "./reducer";
import { ScormPackageManagement } from "./ScormPackageManagement/ScormPackageManagement.container";
import { SelectOrganization } from "./SelectOrganization/SelectOrganization.container";
import ThemeManagement from "./ThemeManagement/ThemeManagement.container";
import { ThemeList } from "./Themes/ThemesList.container";
import { AccountManagement } from "./AccountManagement/AccountManagement.container";

const Creators = (AllCreators as any).App;

export interface IAppOwnProps extends RouteComponentProps<{}> {}

export interface IAppStateProps {
  authenticationState: { isKnown: boolean; isAuthenticated: boolean };
}

export interface IAppDispatchProps {
  onAuthenticationStateKnown: (nextPage: string) => void;
  onMount: () => void;
  onUnmount: () => void;
}

export type AppProps = IAppOwnProps & IAppStateProps & IAppDispatchProps;

interface ILoginRequiredRouteComponentProps {
  component: React.ElementType;
}

interface ILoginRequiredRouteComponentStateProps {
  authenticationState: { isKnown: boolean; isAuthenticated: boolean };
}

type LoginRequiredRouteComponentProps = ILoginRequiredRouteComponentProps & ILoginRequiredRouteComponentStateProps;

const LoginRequiredRouteComponent: React.FC<LoginRequiredRouteComponentProps> = props => {
  const { component: Component, authenticationState, ...rest } = props;
  const renderOrRedirect = (renderProps: RouteComponentProps) =>
    authenticationState.isAuthenticated ? (
      <Component {...renderProps} />
    ) : (
      <Redirect
        to={{
          pathname: "/login",
        }}
      />
    );
  return <Route {...rest} render={renderOrRedirect} />;
};

const LoginRequiredRoute = connect(
  (state: IState): ILoginRequiredRouteComponentStateProps => ({
    authenticationState: state.App._.authenticationState,
  })
)(LoginRequiredRouteComponent);

const PrivateRoutes = () => (
  <Switch>
    <RoutesWithSideMenu>
      <Switch>
        <Route exact={true} path="/analytics/:type(class|scormPackage)/:courseId/:objectId" component={Analytics} />
        <Route exact={true} path="/dashboard" component={Dashboard} />
        <Redirect exact={true} from="/" to="/dashboard" />
        <Route exact={true} path="/courses" component={Courses} />
        <Route exact={true} path="/accounts" component={AccountList} />
        <Route path="/account-management/:accountId" component={AccountManagement} />
        <Route path="/class-management/:classId" component={ClassManagement} />
        <Route path="/course-author/:courseId" component={CourseAuthor} />
        <Route exact={true} path="/classes" component={Classes} />
        <Route exact={true} path="/organization-settings" component={OrganizationSettings} />
        <Route exact={true} path="/themes" component={ThemeList} />
        <Route exact={true} path="/theme-management/:styleId" component={ThemeManagement} />
        <Route exact={true} path="/select-organization" component={SelectOrganization} />
        <Route exact={true} path="/scorm-packages/:scormPackageId" component={ScormPackageManagement} />
        <Route exact={true} path="/exported-scorm-packages" component={ExportedScormPackages} />
        <Route path="*" component={NotFound} />
      </Switch>
    </RoutesWithSideMenu>
  </Switch>
);

export class App extends React.Component<AppProps> {
  public componentDidMount() {
    this.props.onMount();
    const version = window.localStorage.getItem("SALTCOMPLIANCE/version");
    const root = window.localStorage.getItem("SALTCOMPLIANCE/root");
    const organizationId = window.localStorage.getItem("SALTCOMPLIANCE/organizationId");

    if (version && root && organizationId) {
      const { skin } = getSkinUrls(root, organizationId, version);
      const styleDOM = window.document.getElementById(skin.id);

      if (styleDOM) {
        styleDOM.setAttribute("href", skin.url);
      } else {
        // tslint:disable-next-line:no-console
        console.log("No skin or logo was loaded");
      }
    }
  }

  public componentWillUnmount() {
    this.props.onUnmount();
  }

  public render() {
    if (!this.props.authenticationState.isKnown) {
      return <Spinner data-testid="app-spinner" />;
    }
    return (
      <Router history={this.props.history}>
        <Switch>
          <Route exact={true} path="/login" component={Login} />
          <Route exact={true} path="/logout" component={Logout} />
          <LoginRequiredRoute component={PrivateRoutes} />
        </Switch>
      </Router>
    );
  }
}

export function mapStateToProps(state: IState, ownProps: IAppOwnProps): IAppStateProps {
  return {
    authenticationState: state.App._.authenticationState,
  };
}

export function mapDispatchToProps(dispatch: Dispatch<IAction>, ownProps: IAppOwnProps) {
  return {
    onAuthenticationStateKnown: (nextPage: string) => dispatch(push(nextPage)),
    onMount: () => {
      dispatch(Creators.mount());
      dispatch(Creators.fetchLocalAuthentication());
    },
    onUnmount: () => dispatch(Creators.unmount()),
  };
}

export default withRouter(
  connect<IAppStateProps, IAppDispatchProps, IAppOwnProps, IState>(mapStateToProps, mapDispatchToProps)(App)
);
